/*
 * Novium — the engraving add-on in the cart.
 *
 * The rules this file enforces:
 *   1. An engraved pen and its engraving go into the cart together or not at
 *      all. They are sent in ONE request, so a dropped connection can no longer
 *      leave an engraved pen without its engraving.
 *   2. The engraving is never removed or re-counted on its own. Removing the pen
 *      removes it; changing the pen's quantity changes it to match.
 *   3. After every cart change, a repair pass puts the cart back in order: an
 *      engraving with no pen is removed, an engraving quantity that drifted is
 *      corrected, and an engraved pen that lost its engraving gets it back.
 *
 * Loaded on every page, because the cart drawer is on every page.
 *
 * It talks to the rest of the theme only through the theme's own cart events -
 * cart:prepare-bundled-sections, variant:add, cart:change, cart:refresh - so the
 * drawer, the cart count and the free-shipping bar update exactly as they do for
 * any other product. It never wraps fetch or XMLHttpRequest.
 *
 * How a pen and its engraving are linked, on lines added by this file:
 *   pen        visible font + text (translated), _Unique, _EngraveVariant (engraving
 *              variant id), and for fulfilment the same in every language:
 *              _EngFont (Standard / Manuscrit) and _EngText (the engraving text)
 *   engraving  _Engrave: "true", _Parent: "<pen variant id>:<_Unique>", _Unique
 * Older lines (_Engrave + _Parent as the pen's line key) are still recognised.
 */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : this, function (root) {
  'use strict';

  var ENGRAVE = '_Engrave';
  var UNIQUE = '_Unique';
  var PARENT = '_Parent';
  var ENGRAVE_VARIANT = '_EngraveVariant';
  var LOG = '[novium-engraving]';

  // Fulfilment fields: identical in every language, hidden from customers.
  // Read them by their exact name: an export that matches any field name
  // containing "Text" would pick up the engraving text twice.
  var FONT_FIELD = '_EngFont';
  var INSCRIPTION_FIELD = '_EngText';
  var FONT_NAMES = { regular: 'Standard', cursive: 'Manuscrit' };
  // Translated names of the regular font, only used if the button says nothing
  // about the ticked box (a stale cw-script.js still in a browser cache).
  var REGULAR_FONT_WORDS = ['Moderne', 'Standard', 'Tondo', 'Regular', 'Standaard', 'Zwykła'];

  /* ------------------------------------------------------------------------
   * Cart logic. Pure: no page, no network. Tested in tests/.
   * ---------------------------------------------------------------------- */

  function propertiesOf(line) {
    return (line && line.properties) || {};
  }

  function isEngraving(line) {
    return propertiesOf(line)[ENGRAVE] === 'true';
  }

  // A pen carries an engraving when it has a visible property with a value.
  // Hidden properties start with "_". Blank values are the empty labels the
  // previous code wrote onto every pen, engraved or not.
  function carriesEngravingText(line) {
    var properties = propertiesOf(line);
    return Object.keys(properties).some(function (name) {
      var value = properties[name];
      return name.charAt(0) !== '_' && value != null && String(value).trim() !== '';
    });
  }

  function isCurrentPen(line) {
    return !isEngraving(line) && !!propertiesOf(line)[UNIQUE];
  }

  function engravingProperties(penVariantId, unique) {
    var properties = {};
    properties[ENGRAVE] = 'true';
    properties[PARENT] = String(penVariantId) + ':' + unique;
    properties[UNIQUE] = unique;
    return properties;
  }

  /*
   * Match each engraving line to the pen line it belongs to. A pen line owns at
   * most one engraving line, and only pens carrying engraving text qualify.
   * Strongest match first:
   *   1. the same _Unique on both lines        (lines added by this file)
   *   2. _Parent equal to the pen's line key    (older lines)
   *   3. _Parent starting with the pen's variant (older lines whose pen line key
   *      has changed since)
   */
  function pairLines(lines) {
    var pens = [];
    var engravings = [];
    (lines || []).forEach(function (line) {
      (isEngraving(line) ? engravings : pens).push(line);
    });

    var penFor = {};
    var owned = {};

    function claim(engraving, belongs) {
      if (penFor[engraving.key]) return;
      for (var i = 0; i < pens.length; i++) {
        var pen = pens[i];
        if (!owned[pen.key] && carriesEngravingText(pen) && belongs(pen)) {
          penFor[engraving.key] = pen;
          owned[pen.key] = true;
          return;
        }
      }
    }

    engravings.forEach(function (engraving) {
      var unique = propertiesOf(engraving)[UNIQUE];
      if (!unique) return;
      claim(engraving, function (pen) { return propertiesOf(pen)[UNIQUE] === unique; });
    });

    engravings.forEach(function (engraving) {
      var parent = propertiesOf(engraving)[PARENT];
      if (!parent) return;
      claim(engraving, function (pen) { return !isCurrentPen(pen) && pen.key === parent; });
    });

    engravings.forEach(function (engraving) {
      var parent = propertiesOf(engraving)[PARENT];
      if (!parent) return;
      var variant = String(parent).split(':')[0];
      claim(engraving, function (pen) { return !isCurrentPen(pen) && String(pen.variant_id) === variant; });
    });

    return { pens: pens, engravings: engravings, penFor: penFor, owned: owned };
  }

  // What the cart needs to be in order. Returns the writes; performs none.
  function planRepairs(lines) {
    var pairs = pairLines(lines);
    var updates = {};
    var adds = [];
    var notes = [];

    pairs.engravings.forEach(function (engraving) {
      var pen = pairs.penFor[engraving.key];
      if (!pen) {
        updates[engraving.key] = 0;
        notes.push({ type: 'removed_engraving_without_pen', key: engraving.key });
      } else if (engraving.quantity !== pen.quantity) {
        updates[engraving.key] = pen.quantity;
        notes.push({ type: 'matched_engraving_quantity', key: engraving.key, from: engraving.quantity, to: pen.quantity });
      }
    });

    pairs.pens.forEach(function (pen) {
      if (pairs.owned[pen.key] || !isCurrentPen(pen) || !carriesEngravingText(pen)) return;
      var engravingVariant = propertiesOf(pen)[ENGRAVE_VARIANT];
      if (!engravingVariant) return;
      adds.push({
        id: Number(engravingVariant),
        quantity: pen.quantity,
        properties: engravingProperties(pen.variant_id, propertiesOf(pen)[UNIQUE])
      });
      notes.push({ type: 'restored_missing_engraving', key: pen.key });
    });

    return { updates: updates, adds: adds, notes: notes };
  }

  var api = {
    isEngraving: isEngraving,
    pairLines: pairLines,
    planRepairs: planRepairs
  };

  /* ------------------------------------------------------------------------
   * The page. Everything below only runs in a browser.
   * ---------------------------------------------------------------------- */

  if (!root || !root.document) return api;
  if (root.NoviumEngraving) return root.NoviumEngraving;
  root.NoviumEngraving = api;

  var doc = root.document;

  function cartRoot() {
    return (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
  }

  function report(notes) {
    if (!notes || !notes.length) return;
    try { root.console.warn(LOG, notes); } catch (error) { /* never let logging break the cart */ }
  }

  function findLine(lines, key) {
    for (var i = 0; i < (lines || []).length; i++) {
      if (lines[i].key === key) return lines[i];
    }
    return null;
  }

  function request(path, body) {
    var init = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body !== undefined) {
      init.method = 'POST';
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    return root.fetch(cartRoot() + path, init).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (json) {
        if (!response.ok) {
          var error = new Error((json && (json.description || json.message)) || ('HTTP ' + response.status));
          error.status = response.status;
          error.description = json && json.description;
          throw error;
        }
        return json;
      });
    });
  }

  function getCart() {
    return request('cart.js');
  }

  // The theme's way of asking "which sections re-render after a cart change?"
  // The drawer answers by adding its own section id.
  function bundledSections() {
    var sections = [];
    doc.documentElement.dispatchEvent(new root.CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sections }
    }));
    return sections;
  }

  function withSections(cart, sections) {
    if (!sections.length) return Promise.resolve(cart);
    var url = cartRoot() + '?sections=' + sections.map(encodeURIComponent).join(',');
    return root.fetch(url, { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (rendered) {
        if (rendered) cart.sections = rendered;
        return cart;
      })
      .catch(function () { return cart; });
  }

  // Tell the theme the cart changed, the same way its own add flow does: the
  // drawer, the cart count, the free-shipping bar and the theme's cart cache all
  // update from this event.
  //
  // baseEvent is always "variant:add" on purpose. It is the only value for which
  // the drawer redraws its lines immediately; for anything else it waits 1.25s to
  // let the theme's own line-removal animation play, which this file doesn't use -
  // that wait is what made the engraving trail the pen on screen. Nothing else in
  // the theme reads baseEvent, and this does NOT fire the variant:add event that
  // opens the drawer.
  //
  // Without rendered sections the drawer can't use cart:change, so fall back to
  // cart:refresh, which rebuilds it from the server.
  function announce(cart) {
    if (cart && cart.sections && Object.keys(cart.sections).length) {
      doc.documentElement.dispatchEvent(new root.CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cart, noviumEngraving: true }
      }));
    } else {
      doc.dispatchEvent(new root.CustomEvent('cart:refresh'));
    }
  }

  /* ---------------------------------------------------------------- repair */

  var repairing = false;
  var repairAgain = false;

  // Restore a missing engraving at most once per session, so an engraving that
  // can't be added (unavailable in this market, say) never loops.
  function mayRestore(add) {
    var key = 'nvEngravingRestore:' + add.properties[UNIQUE];
    try {
      if (root.sessionStorage.getItem(key)) return false;
      root.sessionStorage.setItem(key, '1');
    } catch (error) { /* storage blocked: allow it, the lack of a loop still holds */ }
    return true;
  }

  function repairCart(knownCart) {
    if (repairing) {
      repairAgain = true;
      return Promise.resolve();
    }
    repairing = true;

    return Promise.resolve(knownCart && knownCart.items ? knownCart : getCart())
      .then(function (cart) {
        var plan = planRepairs(cart.items);
        report(plan.notes);

        var adds = plan.adds.filter(mayRestore);
        var hasUpdates = Object.keys(plan.updates).length > 0;
        if (!hasUpdates && !adds.length) return null;

        var sections = bundledSections();
        var writes = hasUpdates ? request('cart/update.js', { updates: plan.updates }) : Promise.resolve();

        adds.forEach(function (add) {
          writes = writes.then(function () {
            return request('cart/add.js', { items: [add] }).catch(function (error) {
              report([{ type: 'could_not_restore_engraving', status: error.status, error: error.message }]);
            });
          });
        });

        return writes
          .then(getCart)
          .then(function (fresh) { return withSections(fresh, sections); })
          .then(function (fresh) { announce(fresh); });
      })
      .catch(function (error) {
        report([{ type: 'repair_failed', error: String(error && error.message) }]);
      })
      .then(function () {
        repairing = false;
        if (repairAgain) {
          repairAgain = false;
          return repairCart();
        }
        return null;
      });
  }

  /* ----------------------------------------------------------- add to cart */

  function engravingLabels(button) {
    var scope = (button.closest && button.closest('.productmain')) || doc;
    var wrapper = scope.querySelector('.engravebtnwrapper') || doc.querySelector('.engravebtnwrapper');
    var font = wrapper ? wrapper.getAttribute('data-entxt1') : '';
    var text = wrapper ? wrapper.getAttribute('data-entxt2') : '';

    // The labels are theme settings, kept exactly as typed. Blank ones are a
    // settings problem: fall back to the schema defaults rather than lose the text.
    if (!font || !font.trim()) {
      report([{ type: 'font_label_setting_is_blank' }]);
      font = 'Engrave Font';
    }
    if (!text || !text.trim()) {
      report([{ type: 'text_label_setting_is_blank' }]);
      text = 'Engrave Text';
    }
    if (font === text) report([{ type: 'font_and_text_label_settings_are_identical', label: font }]);
    return { font: font, text: text };
  }

  function fixedFontName(style, translatedFont) {
    if (style === 'regular' || style === 'cursive') return FONT_NAMES[style];
    report([{ type: 'font_box_unknown_using_translated_name', font: translatedFont }]);
    return REGULAR_FONT_WORDS.indexOf(translatedFont) !== -1 ? FONT_NAMES.regular : FONT_NAMES.cursive;
  }

  function newUnique() {
    return Date.now() + '_' + Math.random().toString(36).slice(2, 7);
  }

  function clearEngraving() {
    doc.querySelectorAll('.AddToCart').forEach(function (button) {
      button.setAttribute('data-bundle-id', '');
      button.setAttribute('data-custom-text', '');
      button.setAttribute('data-bundle-font', '');
      button.setAttribute('data-engraving-style', '');
    });
    doc.querySelectorAll('.Météoritebox').forEach(function (box) {
      box.classList.remove('gMétéoritebox');
      box.querySelectorAll('input').forEach(function (input) { input.disabled = false; });
    });
    doc.querySelectorAll('.engravbtn').forEach(function (element) { element.style.display = 'flex'; });
    doc.querySelectorAll('.engravingresult').forEach(function (element) { element.style.display = 'none'; });
    doc.querySelectorAll('.viewinput_text').forEach(function (element) { element.style.display = 'none'; });
  }

  // The summary says "engraved" but the button carries no text: send the
  // customer back to the engraving window with its "add your text" message.
  function reopenEngraving() {
    var modal = doc.querySelector('.sizeguideview1');
    if (modal) modal.classList.add('is-visible');
    doc.querySelectorAll('.merror-text').forEach(function (element) { element.style.display = 'block'; });
  }

  function rollBack(unique) {
    return getCart()
      .then(function (cart) {
        var updates = {};
        cart.items.forEach(function (line) {
          if (propertiesOf(line)[UNIQUE] === unique) updates[line.key] = 0;
        });
        return Object.keys(updates).length ? request('cart/update.js', { updates: updates }) : null;
      })
      .catch(function (error) {
        report([{ type: 'rollback_failed', error: String(error && error.message) }]);
      });
  }

  function addToCart(button) {
    if (button.__nvBusy) return Promise.resolve();

    var data = button.dataset || {};
    var mainId = data.mainId;
    var engravingVariant = (data.bundleId || '').trim();
    var font = (data.bundleFont || '').trim();
    var text = (data.customText || '').trim();

    if (!mainId) {
      report([{ type: 'add_to_cart_has_no_variant' }]);
      return Promise.resolve();
    }

    var wantsEngraving = engravingVariant !== '' && engravingVariant !== 'null';
    if (wantsEngraving && (!font || !text)) {
      report([{ type: 'refused_incomplete_engraving', hasFont: !!font, hasText: !!text }]);
      reopenEngraving();
      return Promise.resolve();
    }

    // A pen without an engraving is sent with no properties at all. The previous
    // code sent the two labels with empty values, and Shopify keeps those.
    var penProperties = {};
    var items = [{ id: Number(mainId), quantity: 1, properties: penProperties }];
    var unique = null;

    if (wantsEngraving) {
      var labels = engravingLabels(button);
      unique = newUnique();
      penProperties[labels.font] = font;
      penProperties[labels.text] = text;
      penProperties[FONT_FIELD] = fixedFontName(data.engravingStyle, font);
      penProperties[INSCRIPTION_FIELD] = text;
      penProperties[UNIQUE] = unique;
      penProperties[ENGRAVE_VARIANT] = engravingVariant;
      items.push({ id: Number(engravingVariant), quantity: 1, properties: engravingProperties(mainId, unique) });
    }

    button.__nvBusy = true;
    button.setAttribute('aria-busy', 'true');
    var sections = bundledSections();

    return request('cart/add.js', { items: items, sections: sections })
      .then(function (added) {
        return getCart().then(function (cart) {
          cart.sections = added.sections;
          clearEngraving();
          var origin = (button.closest && button.closest('form')) || button;
          origin.dispatchEvent(new root.CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: added.items || [], cart: cart }
          }));
          announce(cart);
        });
      })
      .catch(function (error) {
        report([{ type: 'add_to_cart_failed', status: error.status, error: error.message }]);
        // If the request reached Shopify but the answer didn't reach us, the pair
        // may be in the cart after all. Take it back out, so a failed add never
        // leaves anything behind.
        return (unique ? rollBack(unique) : Promise.resolve()).then(function () {
          doc.dispatchEvent(new root.CustomEvent('cart:refresh'));
        });
      })
      .then(function () {
        button.__nvBusy = false;
        button.removeAttribute('aria-busy');
      });
  }

  /* ------------------------------------------------- remove and quantity */
  /*
   * Removing an engraved pen, or changing its quantity, is ONE request covering
   * the pen and its engraving, so the two lines always change together - in the
   * cart and on screen. No loading indicator is shown, as before. Plain products
   * are left entirely to the theme.
   *
   * The drawer marks each line (snippets/line-item.liquid): data-nv-key always;
   * data-nv-engraving on engraving lines; data-nv-text on lines carrying
   * engraving text; data-nv-unique on lines added by this file, shared by a pen
   * and its engraving. Older pens have no data-nv-unique, so their engraving is
   * looked up in the cart first.
   */

  function lineKeyOf(element) {
    return element.getAttribute('data-nv-key');
  }

  function distinct(values) {
    return values.filter(function (value, index) { return value && values.indexOf(value) === index; });
  }

  function engravingElementsFor(penElement) {
    var unique = penElement && penElement.getAttribute('data-nv-unique');
    if (!unique) return [];
    var found = [];
    doc.querySelectorAll('line-item[data-nv-engraving]').forEach(function (element) {
      if (element !== penElement && element.getAttribute('data-nv-unique') === unique) found.push(element);
    });
    return found;
  }

  // The keys of the engraving lines belonging to a pen: from the page when the
  // pen carries data-nv-unique, otherwise from the cart. Resolves to null when
  // the line isn't in the cart, or is itself an engraving.
  function engravingKeysFor(penKey, engravingElements) {
    if (engravingElements.length) {
      return Promise.resolve(distinct(engravingElements.map(lineKeyOf)));
    }
    return getCart().then(function (cart) {
      var pen = findLine(cart.items, penKey);
      if (!pen || isEngraving(pen)) return null;
      var pairs = pairLines(cart.items);
      return pairs.engravings
        .filter(function (engraving) {
          var owner = pairs.penFor[engraving.key];
          return owner && owner.key === penKey;
        })
        .map(function (engraving) { return engraving.key; });
    });
  }

  function setPenQuantity(penElement, penKey, quantity) {
    var engravingElements = engravingElementsFor(penElement);
    var sections = bundledSections();

    // Show the engraving's new quantity straight away, next to the pen's.
    engravingElements.forEach(function (element) {
      element.querySelectorAll('input.quantity-input').forEach(function (input) {
        input.value = String(quantity);
      });
    });

    return engravingKeysFor(penKey, engravingElements)
      .then(function (engravingKeys) {
        if (engravingKeys === null) {
          report([{ type: 'line_not_changed', key: penKey }]);
          return getCart().then(function (cart) { return withSections(cart, sections); });
        }
        var updates = {};
        updates[penKey] = quantity;
        engravingKeys.forEach(function (key) { updates[key] = quantity; });
        return request('cart/update.js', { updates: updates, sections: sections });
      })
      .then(function (cart) {
        announce(cart);
        return repairCart(cart);
      })
      .catch(function (error) {
        report([{ type: 'cart_write_failed', key: penKey, error: String(error && error.message) }]);
        doc.dispatchEvent(new root.CustomEvent('cart:refresh'));
        return repairCart();
      });
  }

  function removeLine(button) {
    var key = button.getAttribute('data-line');
    if (!key || button.__nvBusy) return Promise.resolve();

    var penElement = button.closest && button.closest('line-item');
    if (penElement && penElement.hasAttribute('data-nv-engraving')) {
      report([{ type: 'refused_removing_engraving_on_its_own', key: key }]);
      return Promise.resolve();
    }

    button.__nvBusy = true;
    return setPenQuantity(penElement, key, 0).then(function () {
      button.__nvBusy = false;
    });
  }

  // A quantity box on a line carrying engraving: take the write over from the
  // theme, so the pen and its engraving change in the same request. Runs before
  // the theme's own handler (capture phase) and stops it. Same limits as the
  // theme's quantity-input: never below 1, never above its max.
  function takeOverQuantity(event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('input.quantity-input[data-line-key]')) return;
    var penElement = input.closest && input.closest('line-item');
    if (!penElement || penElement.hasAttribute('data-nv-engraving')) return;
    if (!penElement.hasAttribute('data-nv-text') && !engravingElementsFor(penElement).length) return;

    event.stopPropagation();

    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < 1) quantity = 1;
    var max = parseInt(input.getAttribute('max'), 10);
    if (max > 0) quantity = Math.min(quantity, max);
    input.value = String(quantity);

    setPenQuantity(penElement, input.getAttribute('data-line-key'), quantity);
  }

  /* -------------------------------------------------------------- bindings */

  // Delegated from the document, so they survive every drawer re-render.
  doc.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var addButton = target.closest('.ajax-add-to-cart');
    if (addButton) {
      event.preventDefault();
      addToCart(addButton);
      return;
    }

    var removeButton = target.closest('.cart-remove-btn');
    if (removeButton) {
      event.preventDefault();
      removeLine(removeButton);
    }
  });

  // Quantity on a line carrying engraving: one request for the pen and its
  // engraving. Capture phase, so it runs before the theme's <line-item-quantity>.
  doc.addEventListener('change', takeOverQuantity, true);

  // Any other cart change - the theme's own, an app's - is followed by the
  // repair pass.
  doc.addEventListener('cart:change', function (event) {
    if (event.detail && event.detail.noviumEngraving) return;
    repairCart(event.detail && event.detail.cart);
  });

  function repairOnLoad() {
    var drawerReady = root.customElements && root.customElements.whenDefined
      ? Promise.race([
          root.customElements.whenDefined('cart-drawer'),
          new Promise(function (resolve) { root.setTimeout(resolve, 3000); })
        ])
      : Promise.resolve();
    drawerReady.then(function () { repairCart(); });
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', repairOnLoad);
  } else {
    repairOnLoad();
  }

  root.addEventListener('pageshow', function (event) {
    if (event.persisted) repairCart();
  });

  // For the console: NoviumEngraving.inspect() prints each line and its pairing.
  api.repair = repairCart;
  api.inspect = function () {
    return getCart().then(function (cart) {
      var pairs = pairLines(cart.items);
      root.console.table(cart.items.map(function (line) {
        var pen = pairs.penFor[line.key];
        var visible = {};
        Object.keys(propertiesOf(line)).forEach(function (name) {
          if (name.charAt(0) !== '_') visible[name] = propertiesOf(line)[name];
        });
        return {
          title: line.title,
          quantity: line.quantity,
          engraving: isEngraving(line),
          belongsTo: pen ? pen.title : (isEngraving(line) ? 'NO PEN' : ''),
          properties: JSON.stringify(visible),
          key: line.key
        };
      }));
      return cart;
    });
  };

  return api;
});
